import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import type { Severity } from "@/components/safety-config/risk-groups";
import { SEVERITY_LABEL } from "@/components/safety-config/risk-groups";
import { cx } from "@/utils/cx";

// Same colors as Configure Safety's read-only risk rows (TabRisks/RiskRow.tsx) — kept in
// sync manually since that one drives a whole row's layout and isn't reusable as-is here.
// Read-only badge, not a severity picker — color stays local on purpose.
const SEVERITY_TEXT: Record<Severity, string> = {
  critical: "text-error-600",
  high: "text-error-600",
  medium: "text-warning-600",
  low: "text-success-600",
};

const SEVERITY_DOT: Record<Severity, string> = {
  critical: "bg-error-500",
  high: "bg-error-500",
  medium: "bg-warning-500",
  low: "bg-success-500",
};

type RiskSeverityDotProps = {
  severity: Severity;
  // Overrides the default "{Severity} Severity" text — e.g. the risk's own name.
  label?: string;
};

// Dot + text, no border/background — distinct from RiskSeverityPill's badge look. Used in the
// "Risks" column of the With Creator to Action / Risk Resolved tables (Figma UNB-2017).
export const RiskSeverityDot = ({ severity, label }: RiskSeverityDotProps) => {
  const text = label ?? `${SEVERITY_LABEL[severity]} Severity`;

  return (
    <span className="flex items-center gap-1.5 min-w-0">
      <span aria-hidden="true" className={cx("w-1.5 h-1.5 rounded-full shrink-0", SEVERITY_DOT[severity])} />
      <Tooltip title={text}>
        <TooltipTrigger className="inline-flex w-full min-w-0 justify-start">
          <span className={cx("text-xs font-medium truncate", SEVERITY_TEXT[severity])}>{text}</span>
        </TooltipTrigger>
      </Tooltip>
    </span>
  );
};
